import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../common/CustomModal';
import { enqueueSuccessSnackbar } from '../../../action/snackbarCommands';
import {
    invalidateInventoryReaders,
    useReactivateObjectMutation,
    useRetireObjectMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';

export type ObjectLifecycleAction = 'retire' | 'reactivate';

interface Props {
    action: ObjectLifecycleAction;
    assetId: string;
    /** The object as the operator knows it — named in every text. */
    name: string;
    /** A room retires with its cell, and with its product when it was the only room. */
    isRoom: boolean;
    onClose: () => void;
    /** After success — the page refetches its object. */
    onDone?: () => void;
}

/**
 * Confirm and run "Ausmustern" or "Wieder in Betrieb nehmen" for one object
 * (PO, 2026-10-15) — rooms and station objects alike.
 *
 * Like `HardDeleteDialog` it stays open on a refusal and lists the reasons the
 * server named: "has a booking next week" and "is part of a combined room" need
 * different next steps, and a bare "could not be retired" teaches nothing.
 */
const ObjectLifecycleDialog = ({ action, assetId, name, isRoom, onClose, onDone }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [retireObject, { isLoading: isRetiring }] = useRetireObjectMutation();
    const [reactivateObject, { isLoading: isReactivating }] = useReactivateObjectMutation();
    const [reasons, setReasons] = useState<string[]>([]);
    const [detail, setDetail] = useState<string | null>(null);
    const isLoading = isRetiring || isReactivating;
    const prefix = `asset_detail.lifecycle.${action}`;

    const handleConfirm = async () => {
        setReasons([]);
        setDetail(null);
        try {
            if (action === 'retire') {
                await retireObject({ assetId }).unwrap();
            } else {
                await reactivateObject({ assetId }).unwrap();
            }
            // Cross-slice: the overview, the house page and the Services lists
            // live in other slices, which `invalidatesTags` cannot reach.
            invalidateInventoryReaders(dispatch);
            dispatch(enqueueSuccessSnackbar({ message: t(`${prefix}.success`, { name }), autoHideDuration: 4000 }));
            onClose();
            onDone?.();
        } catch (err) {
            const failure = readInventoryFailure(err);
            if (failure.blockers.length > 0) {
                setReasons(failure.blockers);
                return;
            }
            setDetail(failure.unreachable
                ? t('asset_detail.lifecycle.error_unreachable')
                : (failure.message ?? t('asset_detail.lifecycle.error_service')));
        }
    };

    const hasFailure = reasons.length > 0 || !!detail;

    return (
        <CustomModal
            open={true}
            title={t(`${prefix}.confirm_title`)}
            description={name}
            onClose={onClose}
            closeButton={<CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />}
            saveButton={
                <CustomButton
                    title={t(`${prefix}.confirm_cta`)}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleConfirm}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/70'}>
                    {t(isRoom ? `${prefix}.confirm_body_room` : `${prefix}.confirm_body`, { name })}
                </p>
                {hasFailure && (
                    <div
                        className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                            + ' px-4 py-3 text-sm text-foreground/70'}
                        role={'alert'}
                    >
                        <p className={'font-medium text-foreground mb-1'}>{t(`${prefix}.failed_title`, { name })}</p>
                        {reasons.length > 0 && (
                            <ul className={'list-disc pl-5 flex flex-col gap-1'}>
                                {reasons.map((reason) => (
                                    <li key={reason}>
                                        {t(`asset_detail.lifecycle.blocker.${reason}`, {
                                            defaultValue: t('asset_detail.lifecycle.blocker.unknown'),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {detail && <p>{detail}</p>}
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default ObjectLifecycleDialog;
